import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { 
  ArrowLeft, 
  Save, 
  Plus,
  X,
  FileText,
  Trash2
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const AddBulletPoints = () => {
  const navigate = useNavigate()
  const { bulletId } = useParams()
  
  const [bullet, setBullet] = useState(null)
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [bulletPoints, setBulletPoints] = useState([''])
  
  const { getNursingBullets, updateNursingBullet } = useFirebase()

  useEffect(() => {
    loadBullet()
  }, [bulletId])

  const loadBullet = async () => {
    try {
      setInitialLoading(true)
      const bullets = await getNursingBullets()
      const foundBullet = bullets.find(b => b.id === bulletId)
      if (foundBullet) {
        setBullet(foundBullet)
        // Load existing bullet points if any
        if (foundBullet.bulletPoints && foundBullet.bulletPoints.length > 0) {
          setBulletPoints(foundBullet.bulletPoints)
        }
      } else {
        toast.error('Nursing bullet not found')
        navigate('/nursing-bullets')
      }
    } catch (error) {
      console.error('Error loading bullet:', error)
      toast.error('Failed to load nursing bullet')
      navigate('/nursing-bullets')
    } finally {
      setInitialLoading(false)
    }
  }

  const addBulletPoint = () => {
    setBulletPoints([...bulletPoints, ''])
  }

  const removeBulletPoint = async (index) => {
    if (bulletPoints.length > 1) {
      const updatedPoints = bulletPoints.filter((_, i) => i !== index)
      setBulletPoints(updatedPoints)
      
      // Immediately save to Firestore after removing
      try {
        const validBulletPoints = updatedPoints.filter(point => point.trim() !== '')
        
        const updatedBullet = {
          ...bullet,
          bulletPoints: validBulletPoints,
          bulletCount: validBulletPoints.length,
          updatedAt: Date.now()
        }

        await updateNursingBullet(bulletId, updatedBullet)
        toast.success('Bullet point deleted successfully!')
        
        // Update local bullet state to reflect changes
        setBullet(updatedBullet)
      } catch (error) {
        console.error('Error deleting bullet point:', error)
        toast.error('Failed to delete bullet point')
        // Revert the local state if Firestore update fails
        setBulletPoints(bulletPoints)
      }
    }
  }

  const updateBulletPoint = (index, value) => {
    const updatedPoints = [...bulletPoints]
    updatedPoints[index] = value
    setBulletPoints(updatedPoints)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Filter out empty bullet points
    const validBulletPoints = bulletPoints.filter(point => point.trim() !== '')
    
    if (validBulletPoints.length === 0) {
      toast.error('Please add at least one bullet point')
      return
    }

    try {
      setLoading(true)
      
      const updatedBullet = {
        ...bullet,
        bulletPoints: validBulletPoints,
        bulletCount: validBulletPoints.length,
        updatedAt: Date.now()
      }

      await updateNursingBullet(bulletId, updatedBullet)
      toast.success('Bullet points saved successfully!')
      navigate('/nursing-bullets')
    } catch (error) {
      console.error('Error saving bullet points:', error)
      toast.error('Failed to save bullet points')
    } finally {
      setLoading(false)
    }
  }

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  if (!bullet) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Bullet not found</h3>
        <button onClick={() => navigate('/nursing-bullets')} className="btn-primary">
          Back to Nursing Bullets
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/nursing-bullets')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Add Bullet Points</h1>
            <p className="text-sm text-gray-600">For: {bullet.title}</p>
          </div>
        </div>
      </div>

      {/* Bullet Info */}
      <div className="card p-6">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <FileText className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">{bullet.title}</h3>
            <p className="text-sm text-gray-600">
              Current bullet count: {bullet.bulletCount || 0}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Bullet Points</h3>
            <button
              type="button"
              onClick={addBulletPoint}
              className="btn-secondary flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Add Point</span>
            </button>
          </div>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Bullet Points */}
            <div className="space-y-4">
              {bulletPoints.map((point, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mt-2">
                    <span className="text-sm font-medium text-blue-600">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <textarea
                      value={point}
                      onChange={(e) => updateBulletPoint(index, e.target.value)}
                      placeholder={`Enter bullet point ${index + 1}...`}
                      className="input w-full"
                      rows={3}
                    />
                  </div>
                  {bulletPoints.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeBulletPoint(index)}
                      className="flex-shrink-0 p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors mt-1"
                      title="Remove bullet point"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Total bullet points:</span>
                <span className="font-medium text-gray-900">
                  {bulletPoints.filter(point => point.trim() !== '').length}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate('/nursing-bullets')}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex items-center space-x-2"
              >
                {loading ? (
                  <LoadingSpinner size="small" />
                ) : (
                  <>
                    <Save className="h-5 w-5" />
                    <span>Save Bullet Points</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default AddBulletPoints
